<template>
  <div>
    <p class="text-2xl mb-6">
      Audience par source
      <dag-info :dags="['ga_get_and_store_source_medium_monthly_v2_dag']" />
    </p>
    <v-card class="pt-8">
      <filters></filters>
    </v-card>

    <v-card class="mt-8">
      <v-card-text class="d-flex flex-wrap pb-4">
        <div class="results-container d-flex flex-row">
          <v-data-table
            :loading="loading"
            :headers="headers"
            :items="items"
            class="elevation-1 results-table"
          >
          </v-data-table>
          <!-- 
            @exportCsv="exportCSV"
            :csvButton="{
              csvData,
              filename: `export_${this.getYear}_audience_source.csv`,
              disabled: exportDisabled,
            }"
          -->
          <dot-menu
            @exportExcel="exportExcel"
            :excelButton="{
              excelData,
              filename: `export_${this.getYear}_audience_source.xlsx`,
              disabled: exportDisabled,
            }"
          />
        </div>
      </v-card-text>
      <progress-bar :download="download" />
    </v-card>
    <div class="text-center">
      <v-snackbar v-model="snackbar" :timeout="timeout" color="success">
        Vous recherchez les sources contenant :
        <strong>{{ this.getContainsSource }}</strong>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import useAxios from "@/hooks/useAxios";

import DagInfo from "@/components/common/menus/DagInfo";
import DotMenu from "@/components/common/menus/DotMenu";
import ProgressBar, {
  download_initial_state,
} from "@/components/common/ProgressBar";
import Filters from "@/components/audience/by-source/filters/Filters.vue";

import { saveRmraView } from "@/utils/rmr-analytics";

export default {
  name: "AudienceParSource",
  components: {
    DagInfo,
    DotMenu,
    Filters,
    ProgressBar,
  },
  data() {
    return {
      items: [],
      snackbar: false,
      timeout: 3000,
      // csvData: "",
      excelData: new Blob([""], { type: "text/plain" }),
      download: download_initial_state(),
      loading: false,
    };
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  mounted() {
    saveRmraView(this.$route);
  },
  methods: {
    resetDownload() {
      this.download = download_initial_state();
    },
    getEndpointParams() {
      if (
        this.getSites.length > 0 &&
        this.getYear &&
        this.sourceMediumId.length > 0
      ) {
        return {
          start: this.start,
          end: this.end,
          source_medium_id: this.sourceMediumId,
          sites_id: this.getSites.join(","),
        };
      }

      return null;
    },
    async getData() {
      const params = this.getEndpointParams();
      if (this.applyFilters && !this.loading && params) {
        this.loading = true;
        const response = await this.axiosGet("/ga-source-sessions/", params);

        this.loading = false;

        this.items = response.data.map((source) => {
          const site = this.getAllSites.find(
            (_site) => _site.id === source.site_id
          );

          return {
            ...source,
            site_name: site?.name ?? "",
            selected: true,
          };
        });

        if (this.getExactSearch === true) {
          this.snackbar = true;
        }

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        this.$store.dispatch("common/updateApplyFilters", false);
      }
    },

    updateYear() {
      this.start = `${this.getYear}-01-01`;
      this.end = `${this.getYear}-12-31`;
    },

    /* async exportCSV() {
      const params = this.getEndpointParams();
      if (params) {
        this.loading = true;
        const { data } = await this.axiosGet(
          "/ga-source-sessions/export_csv",
          params
        );

        this.csvData = data;
        this.loading = false;
      }
    }, */

    async exportExcel() {
      const params = this.getEndpointParams();
      if (params) {
        this.loading = true;
        this.download.running = true;

        try {
          const { data } = await this.axiosGet(
            "/ga-source-sessions/export_xlsx",
            params,
            {
              responseType: "blob",
              onDownloadProgress: (progressEvent) => {
                this.download.percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
              },
            }
          );

          this.excelData = data;
        } catch (error) {
          console.error("error while fetching data");
          this.resetDownload();
        }

        this.loading = false;
      }
    },
  },
  computed: {
    applyFilters() {
      return this.$store.getters["common/getApplyFilters"];
    },
    getSites() {
      return this.$store.getters["audience/getSites"];
    },
    getAllSites() {
      return this.$store.getters["common/getSiteList"];
    },
    getYear() {
      return this.$store.getters["audience/getYearDate"];
    },
    getSourceId() {
      return this.$store.getters["audience/getSourceId"];
    },
    getListSourceId() {
      return this.$store.getters["audience/getListSourceId"];
    },
    getExactSearch() {
      return this.$store.getters["audience/getExactSearch"];
    },
    getContainsSource() {
      return this.$store.getters["audience/getContainsSource"];
    },
    sourceMediumId() {
      return this.getExactSearch
        ? this.getListSourceId.join(",")
        : this.getSourceId.toString();
    },
    headers() {
      return [
        { text: "Site", value: "site_name" },
        { text: "Janvier", value: `${this.getYear}-01` },
        { text: "Février", value: `${this.getYear}-02` },
        { text: "Mars", value: `${this.getYear}-03` },
        { text: "Avril", value: `${this.getYear}-04` },
        { text: "Mai", value: `${this.getYear}-05` },
        { text: "Juin", value: `${this.getYear}-06` },
        { text: "Juillet", value: `${this.getYear}-07` },
        { text: "Août", value: `${this.getYear}-08` },
        { text: "Septembre", value: `${this.getYear}-09` },
        { text: "Octobre", value: `${this.getYear}-10` },
        { text: "Novembre", value: `${this.getYear}-11` },
        { text: "Décembre", value: `${this.getYear}-12` },
      ];
    },
    exportDisabled() {
      return this.loading || this.items.length === 0;
    },
  },
  watch: {
    getYear: {
      handler: function () {
        this.updateYear();
      },
      immediate: true,
    },
    applyFilters() {
      this.getData();
    },
    ["download.percentCompleted"]() {
      if (this.download.percentCompleted >= 100) {
        setTimeout(() => {
          this.resetDownload();
        }, 500);
      }
    },
  },
};
</script>

<style scoped>
.results-container,
.results-table {
  width: 100%;
}
</style>
